import React, { useCallback, useEffect, useRef, useState } from "react";
import { Snackbar, Alert, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";

// Controlla periodicamente /version.json (servito senza cache). Il file viene
// rigenerato ad ogni build: se la versione cambia rispetto a quella caricata,
// mostra un banner con il tasto per fare l'hard refresh (unregister SW + clear
// cache + reload), così l'utente passa subito al nuovo bundle.

const POLL_MS = 60_000;

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}

async function hardReload(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best-effort: procedi comunque al reload */
  }
  window.location.reload();
}

export function VersionWatcher() {
  const { t } = useTranslation();
  const baseline = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const check = useCallback(async () => {
    const v = await fetchVersion();
    if (!v) return;
    if (baseline.current === null) {
      baseline.current = v; // prima lettura = versione del bundle in esecuzione
      return;
    }
    if (v !== baseline.current) setUpdateAvailable(true);
  }, []);

  useEffect(() => {
    void check();
    const id = window.setInterval(() => void check(), POLL_MS);
    const onFocus = () => void check();
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  return (
    <Snackbar
      open={updateAvailable}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: "center", boxShadow: 6 }}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void hardReload()}
            sx={{ borderColor: "rgba(255,255,255,0.6)", fontWeight: 700 }}
          >
            {t("common.updateNow")}
          </Button>
        }
      >
        {t("common.newVersionAvailable")}
      </Alert>
    </Snackbar>
  );
}
